// utils
import { httpClaims } from 'utils/http'
// types
import {
  IClaim,
  IClaimComments,
  IComments,
  IGetClaimsHistoryVersion,
  IGetClaimsResponse,
  ILevelOfSupport,
  ISupportedOperations,
  IsActivePhoneNumber,
  MaintenanceNotification,
} from 'features/claims/types'
import { IEntityBag } from 'features/entities/types'
import { formatAppealStatusDescription } from 'utils/dataExtractor'
// paths
import { apiPaths } from 'configs/apiConfigs'
import { IBulkCalls } from 'features/calls/types'
import {
  ICsvPreview,
  ICsvPreviewRequest,
  ICsvPreviewSniffRequest,
  IDDFColumnSetting,
  IKvpMap,
  IKvpMapGetKey,
} from '../settings/settingsTypes'

export const getClaims = async (url: string): Promise<IGetClaimsResponse> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const exportPDF = async (eobId: string): Promise<any> => {
  const data = await httpClaims.get(apiPaths.exportPdf(eobId), {
    headers: { accept: 'application/octet-stream' },
    responseType: 'arraybuffer',
  })
  return data
}

export const getClaimDetails = async (url: string): Promise<any> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const updateClaimEntityBag = async (oaiClaimId: string, entityBag: IEntityBag): Promise<IClaim> => {
  const { data } = await httpClaims.patch(apiPaths.claimCurrentEntityBag(oaiClaimId), entityBag)
  return data
}

export const archiveClaim = async (claimDetails: any): Promise<any> => {
  const { data } = await httpClaims.patch(apiPaths.claimArchiveEntityBag(), claimDetails)
  return data
}

export const bulkOwner = async (claimDetails: any): Promise<IClaim> => {
  const { data } = await httpClaims.patch(apiPaths.claimBulkOwnerhsip(), claimDetails)
  return data
}

export const getLevelofSupport = async (): Promise<ILevelOfSupport[]> => {
  const { data } = await httpClaims.get(apiPaths.levelOfSupports())
  return data
}

export const getSupportedOperations = async (): Promise<ISupportedOperations[]> => {
  const { data } = await httpClaims.get(apiPaths.supportedOperations())
  return data
}

export interface IEnumValue {
  name: string
  value: number
  description: string
}

export const getEnumValues = async (enumName: string): Promise<IEnumValue[]> => {
  const { data } = await httpClaims.get(apiPaths.enumValues(enumName))
  return data
}

export const getClaimEntityBag = async (url: string): Promise<IEntityBag> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const getClaimEntityBagHistory = async (url: string): Promise<any> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const getClaimCountByType = async (url: string): Promise<IGetClaimsResponse> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const getClaimEntityVersion = async (url: string): Promise<IGetClaimsHistoryVersion> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const getClaimComments = async (url: string): Promise<IClaimComments> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const addClaimComment = async (oaiClaimId: string, comment: IComments): Promise<IComments> => {
  const { data } = await httpClaims.post(apiPaths.claimComments(oaiClaimId), comment)
  return data
}

export const startBulkCalls = async (callRequest: IBulkCalls): Promise<any> => {
  const { data } = await httpClaims.patch(apiPaths.batchCalls(), { ...callRequest })
  return data
}

export const getKvpMapKey = async (url: string): Promise<IKvpMapGetKey> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const postKvpMapKey = async (key: string, kvpMap: IKvpMap) => {
  const { data } = await httpClaims.post(apiPaths.kvpPatchPostMapKey(key), kvpMap)
  return data
}

export const patchKvpMapKey = async (key: string, kvpMap: IKvpMap) => {
  const { data } = await httpClaims.post(apiPaths.kvpPatchPostMapKey(key), kvpMap)
  return data
}

export const getCsvPreviewSniff = async (csvPreviewSniffRequest: ICsvPreviewSniffRequest): Promise<ICsvPreview> => {
  const { data } = await httpClaims.post(apiPaths.csvPreviewSniff(), csvPreviewSniffRequest)
  return data
}

export const getCsvPreview = async (csvPreviewRequest: ICsvPreviewRequest): Promise<ICsvPreview> => {
  const { data } = await httpClaims.post(apiPaths.csvPreview(), csvPreviewRequest)
  return data
}

export const getDdfColumSettingsAvailable = async (): Promise<IDDFColumnSetting[]> => {
  const { data } = await httpClaims.get(apiPaths.ddfColumSettingsAvailable())
  return data
}

export const getPayerAvailability = async (url: string): Promise<IsActivePhoneNumber> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const getCallJobClaimData = async (url: string): Promise<string[]> => {
  const { data } = await httpClaims.get(url)
  return data
}

export const getMaintenanceNotification = async (url: string): Promise<MaintenanceNotification[]> => {
  const { data } = await httpClaims.get(url)
  return data?.data
}

export type ClaimStatusOption = {
  code: number
  value: string
  name: string
}

export const CLAIM_STATUS_PAYER_SLOT = 'claim_status_payer'
export const DETAILED_CLAIM_STATUS_SLOT = 'detailed_claim_status'

const toStatusOptions = (payload: any): ClaimStatusOption[] => {
  const rows = Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? []
  if (!Array.isArray(rows)) return []

  return rows
    .map((item: any, index: number): ClaimStatusOption | null => {
      if (item == null) return null
      if (typeof item === 'string') {
        return { code: index, value: item, name: item }
      }

      const code = typeof item.code === 'number' ? item.code : Number(item.code)
      const name = item.name || item.description || item.label || ''
      const value = item.value || ''
      if (!Number.isFinite(code) || !name) return null
      return { code, value, name }
    })
    .filter((item): item is ClaimStatusOption => item != null)
}

const compactStatusText = (text: string) =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const findStatusOption = (options: ClaimStatusOption[], raw: unknown): ClaimStatusOption | undefined => {
  if (raw == null || raw === '' || !options.length) return undefined

  const text = String(raw).trim()
  if (!text || text === '---') return undefined

  const asCode = Number(text)
  if (typeof raw === 'number' || (Number.isFinite(asCode) && String(asCode) === text)) {
    const byCode = options.find((option) => option.code === asCode)
    if (byCode) return byCode
  }

  const needle = text.toLowerCase()
  const compactNeedle = compactStatusText(text)
  return options.find(
    (option) =>
      option.name.trim().toLowerCase() === needle ||
      option.value.trim().toLowerCase() === needle ||
      compactStatusText(option.name) === compactNeedle ||
      compactStatusText(option.value) === compactNeedle,
  )
}

const optionToCode = (options: ClaimStatusOption[], selected: string): number | null =>
  findStatusOption(options, selected)?.code ?? null

let claimStatusOptions: ClaimStatusOption[] = []
let detailedClaimStatusOptions: ClaimStatusOption[] = []
let appealStatusOptions: ClaimStatusOption[] = []

export const mapClaimStatusLabelsToCodes = (values: string[]): Array<number | null> =>
  values.flatMap((value) => {
    if (value === 'Blank' || value == null) return [null]
    const code = optionToCode(claimStatusOptions, value)
    if (code == null) console.error(`Extracted Claim Status filter: no enum code found for "${value}"`)
    return code == null ? [] : [code]
  })

export const mapDetailedClaimStatusLabelsToCodes = (values: string[]): Array<number | null> =>
  values.flatMap((value) => {
    if (value === 'Blank' || value == null) return [null]
    const code = optionToCode(detailedClaimStatusOptions, value)
    if (code == null) console.error(`Extracted Status Details filter: no enum code found for "${value}"`)
    return code == null ? [] : [code]
  })

export const mapAppealStatusLabelsToCodes = (values: string[]): Array<number | null> =>
  values.flatMap((value) => {
    if (value === 'Blank' || value == null) return [null]
    const code = optionToCode(appealStatusOptions, value)
    if (code == null) console.error(`Extracted Appeal Status filter: no enum code found for "${value}"`)
    return code == null ? [] : [code]
  })

// Filters are sent as codes, so the lookups must be loaded before a claims query is built
export const ensureClaimStatusLookups = async (): Promise<void> => {
  await Promise.all([
    claimStatusOptions.length ? undefined : getClaimStatusLookup(),
    detailedClaimStatusOptions.length ? undefined : getDetailedClaimStatusLookup(),
    appealStatusOptions.length
      ? undefined
      : getAppealStatusValues().catch((error) =>
          console.error('Error loading appeal status codes for filters:', error),
        ),
  ])
}

export const getAppealStatusValues = async (): Promise<IEnumValue[]> => {
  const values = await getEnumValues('AppealStatus')
  appealStatusOptions = (values ?? []).map(({ value, name, description }) => ({
    code: value,
    value: name ?? '',
    name: formatAppealStatusDescription(description || name || ''),
  }))
  return values
}

export const getAppealStatusOptions = async (): Promise<ClaimStatusOption[]> => {
  if (!appealStatusOptions.length) await getAppealStatusValues()
  return appealStatusOptions
}

export const getAppealStatuses = async (): Promise<string[]> => {
  const options = await getAppealStatusOptions()
  return options.map((option) => option.name)
}

export const getClaimStatusLookup = async (): Promise<ClaimStatusOption[]> => {
  const { data } = await httpClaims.get(apiPaths.claimStatuses())
  claimStatusOptions = toStatusOptions(data)
  return claimStatusOptions
}

export const getDetailedClaimStatusLookup = async (): Promise<ClaimStatusOption[]> => {
  const { data } = await httpClaims.get(apiPaths.detailedClaimStatuses())
  detailedClaimStatusOptions = toStatusOptions(data)
  return detailedClaimStatusOptions
}

export const getClaimStatuses = async (): Promise<string[]> => {
  const options = await getClaimStatusLookup()
  return options.map((option) => option.name)
}

export const getDetailedClaimStatuses = async (): Promise<string[]> => {
  const options = await getDetailedClaimStatusLookup()
  return options.map((option) => option.name)
}

export const resolveStatusOption = (
  options: ClaimStatusOption[],
  raw: unknown,
  code?: unknown,
): ClaimStatusOption | undefined => findStatusOption(options, code) || findStatusOption(options, raw)

export const resolveStatusDisplayName = (options: ClaimStatusOption[], raw: unknown, code?: unknown): string => {
  const matched = resolveStatusOption(options, raw, code)
  if (matched) return matched.name
  if (raw == null) return ''
  return String(raw).trim()
}

export const normalizeAppealStatusValues = (values: unknown[]): string[] =>
  values.map((value) => resolveStatusDisplayName(appealStatusOptions, value))

export const formatStatusWithCode = (code: number | null | undefined, name: string): string =>
  code == null ? name : `${code} - ${name}`

const formatStatusLabel = (options: ClaimStatusOption[], name: string): string => {
  const matched = findStatusOption(options, name)
  return matched ? formatStatusWithCode(matched.code, matched.name) : name
}

export const formatClaimStatusLabel = (name: string): string => formatStatusLabel(claimStatusOptions, name)

export const formatDetailedClaimStatusLabel = (name: string): string =>
  formatStatusLabel(detailedClaimStatusOptions, name)

export const resolveExtractedStatusDisplay = (
  slot: string | undefined,
  raw: unknown,
  claimStatuses: ClaimStatusOption[],
  detailedStatuses: ClaimStatusOption[],
  codes?: { claimStatusCode?: unknown; detailedClaimStatusCode?: unknown },
): string => {
  if (slot === CLAIM_STATUS_PAYER_SLOT) {
    return resolveStatusDisplayName(claimStatuses, raw, codes?.claimStatusCode)
  }
  if (slot === DETAILED_CLAIM_STATUS_SLOT) {
    return resolveStatusDisplayName(detailedStatuses, raw, codes?.detailedClaimStatusCode)
  }
  return raw == null ? '' : String(raw)
}
